// Alt Surface Builder compute engine (IM-2487).
//
// Pure, transport-free reproduction of the backend generated-token ATM
// methodology. Given the daily-close frame, the three parameters, the ETH and
// BTC reference surfaces, a token spot and an as-of instant, it produces the
// historical-vol ratios and the per-pillar ATM term structure for the token.
//
// Steps 2–6 reproduce the reference pipeline exactly (pandas SAMPLE std, pandas
// EWMA, the token/ETH ratio, linear-in-T scale). Step 1 DELIBERATELY DEPARTS from
// the production generator: that generator uses simple pct_change, this uses LOG
// returns — the standard additive basis for a vol calculation — so the sandbox
// diverges slightly from the live published surface until the publisher adopts
// log returns too (epic IM-2500). Steps 4–5 also expose optional exploratory
// variants (separate short/long windows; geometric/power scale curves).
//   1. returns  = LOG returns ln(p_t / p_{t-1}) — see note above (reference uses
//                 simple pct_change)
//   2. stdev    = rolling(window).std() — pandas SAMPLE std (ddof=1)
//   3. EWMA     = stdev.ewm(halflife).mean() — pandas adjust=True, ignore_na=False
//   4. ratios   = token last-EWMA / ETH last-EWMA, for short and long half-lives
//                 (each leg may use its own rolling window)
//   5. scale(T) = linear in T across the ETH pillar grid (short_ratio at the front
//                 pillar, long_ratio at the back) — or a geometric/power curve
//   6. ATM      = ETH_ATMF(T) * scale(T); Future = tokenSpot / BTC_DF(T)
//
// Wings (SABR) are out of scope for this pass — ATM only.
//
// The EWMA is the subtle part: it must equal pandas' last value exactly. See
// ewmLast below. The maths here is cross-checked against pandas on a real
// surface fixture in __tests__/alt-surface-compute.test.ts.
import { DAILY_CLOSE_COLUMNS, DailyCloseRow } from './alt-surface-daily-closes';
import { AltSurfaceParams } from './alt-surface-constants';

// ---------------------------------------------------------------------------
// Primitive statistical helpers (each independently unit-tested)
// ---------------------------------------------------------------------------

// Simple percentage change, matching pandas Series.pct_change(): the first
// element is NaN (no prior observation), thereafter value_t / value_{t-1} - 1.
export const pctChange = (values: number[]): number[] => {
  const out = new Array<number>(values.length);
  out[0] = NaN;
  for (let i = 1; i < values.length; i += 1) {
    const prev = values[i - 1];
    out[i] = prev === 0 ? NaN : values[i] / prev - 1;
  }
  return out;
};

// Log (continuously-compounded) returns: ln(value_t / value_{t-1}). The first
// element is NaN (no prior observation); a non-positive price yields NaN (log
// undefined). This is the realised-vol pipeline's return basis — additive log
// returns are the standard for a volatility calculation. (pctChange is kept as
// the pandas-matching simple-return primitive for anything that still needs it.)
export const logReturns = (values: number[]): number[] => {
  if (values.length === 0) return [];
  const out = new Array<number>(values.length);
  out[0] = NaN;
  for (let i = 1; i < values.length; i += 1) {
    const prev = values[i - 1];
    const cur = values[i];
    out[i] = prev > 0 && cur > 0 ? Math.log(cur / prev) : NaN;
  }
  return out;
};

// Rolling SAMPLE standard deviation (ddof=1), matching pandas
// Series.rolling(window).std() with the default min_periods=window: a window is
// only scored once it holds `window` finite observations, so any NaN inside the
// window (including the pct_change warm-up NaN) yields NaN at that position.
export const rollingStd = (values: number[], window: number): number[] => {
  const n = values.length;
  const out = new Array<number>(n).fill(NaN);
  if (window < 2) return out; // ddof=1 is undefined for window < 2
  for (let end = window - 1; end < n; end += 1) {
    const start = end - window + 1;
    let sum = 0;
    let complete = true;
    for (let j = start; j <= end; j += 1) {
      const v = values[j];
      if (!Number.isFinite(v)) {
        complete = false;
        break;
      }
      sum += v;
    }
    if (!complete) continue;
    const mean = sum / window;
    let sq = 0;
    for (let j = start; j <= end; j += 1) {
      const d = values[j] - mean;
      sq += d * d;
    }
    out[end] = Math.sqrt(sq / (window - 1));
  }
  return out;
};

// The LAST value of pandas Series.ewm(halflife=h).mean() with the pandas
// defaults adjust=True and ignore_na=False.
//
// alpha = 1 - 0.5^(1/h); the weight on the observation i steps back from the
// end is (1-alpha)^i. With adjust=True the result is the weighted mean
//   sum_i w_i * x_{n-i} / sum_i w_i.
// With ignore_na=False a NaN observation is EXCLUDED from both sums but its
// position still advances the decay exponent — so leading warm-up NaNs and any
// interior gaps consume a decay step rather than being collapsed away. That
// distinction is exactly what makes this match pandas rather than merely
// resemble it.
export const ewmLast = (values: number[], halflife: number): number => {
  const alpha = 1 - Math.pow(0.5, 1 / halflife);
  const decay = 1 - alpha;
  const n = values.length;
  let num = 0;
  let den = 0;
  let weight = 1; // (1-alpha)^0 on the most recent observation
  for (let i = 0; i < n; i += 1) {
    const v = values[n - 1 - i];
    if (Number.isFinite(v)) {
      num += weight * v;
      den += weight;
    }
    weight *= decay; // position advances even across a NaN (ignore_na=False)
  }
  return den === 0 ? NaN : num / den;
};

// The last EWMA value of the rolling-std of the LOG returns of a close
// series — the full short/long historical-vol pipeline for one column.
export const historicalVolLast = (closes: number[], windowSize: number, halflife: number): number =>
  ewmLast(rollingStd(logReturns(closes), windowSize), halflife);

// The FULL pandas Series.ewm(halflife, adjust=True, ignore_na=False).mean()
// series — the smoothed value at EVERY position, not just the end. ewmLast
// gives the terminal value; the charts need the whole path.
//
// This is the pandas recursive form, which is what makes it match at every
// point rather than only at the end:
//   - The running weighted mean is seeded by the first finite observation; no
//     decay is consumed by the leading warm-up NaNs ahead of it.
//   - Once seeded, each subsequent step decays the accumulated weight by
//     (1-alpha). With ignore_na=False that decay advances even on a NaN
//     observation (the value is carried forward, the weight still decays), so
//     an interior gap consumes a decay step exactly as pandas does.
//   - adjust=True fixes the incoming weight at 1 and reports the weighted mean
//     sum(w_i x)/sum(w_i) at each step.
// Positions before the first observation are NaN (min_periods=1).
export const ewmSeries = (values: number[], halflife: number): number[] => {
  const n = values.length;
  const out = new Array<number>(n).fill(NaN);
  if (n === 0) return out;
  const decay = Math.pow(0.5, 1 / halflife); // = 1 - alpha, the old-weight factor
  const newWt = 1; // adjust=True: each incoming observation enters at unit weight
  let weighted = NaN; // running weighted mean; NaN until the first observation
  let oldWt = 1;
  let seen = false;
  for (let i = 0; i < n; i += 1) {
    const cur = values[i];
    const isObs = Number.isFinite(cur);
    if (Number.isFinite(weighted)) {
      oldWt *= decay; // ignore_na=False: decay advances even across a NaN
      if (isObs) {
        weighted = (oldWt * weighted + newWt * cur) / (oldWt + newWt);
        oldWt += newWt;
      }
    } else if (isObs) {
      weighted = cur; // first observation seeds the mean; no decay consumed yet
    }
    if (isObs) seen = true;
    out[i] = seen ? weighted : NaN;
  }
  return out;
};

// The series behind the stdev-vs-EWMA overlay (shape; see altVolSeries below for
// how each is computed). Four arrays, all index-aligned to the input closes (NaN
// through the warm-up): the short- and long-window rolling stdevs and their
// EWMAs. Vols are raw log-return rolling stdevs (a fraction); annualisation
// (×√365) is a display concern left to the caller.
export interface AltVolSeries {
  stdShort: number[];
  stdLong: number[];
  ewmShort: number[];
  ewmLong: number[];
}

// The short- and long-window rolling stdevs and their EWMA paths for one close
// column. The short leg uses windowShort + hlShort, the long leg windowLong +
// hlLong — the two legs that drive the short/long ratios. All arrays are
// index-aligned to the input closes (NaN through the warm-up).
export const altVolSeries = (
  closes: number[],
  windowShort: number,
  hlShort: number,
  windowLong: number,
  hlLong: number,
): AltVolSeries => {
  const returns = logReturns(closes);
  const stdShort = rollingStd(returns, windowShort);
  const stdLong = rollingStd(returns, windowLong);
  return {
    stdShort,
    stdLong,
    ewmShort: ewmSeries(stdShort, hlShort),
    ewmLong: ewmSeries(stdLong, hlLong),
  };
};

// ---------------------------------------------------------------------------
// Term-structure helpers
// ---------------------------------------------------------------------------

const YEAR_MS = 365 * 24 * 3600 * 1000;

// Hours per day — the day→period conversion for the hourly-return path. The
// day-denominated params (rolling window, EWMA half-lives) are multiplied by
// this to count hourly PERIODS; the sliders stay labelled in days.
const HOURS_PER_DAY = 24;

// Per-period realised std -> annualised vol. The hvols are per-RETURN stdevs, so
// the factor is √(periods per year): √365 for daily returns, √(365×24) for
// hourly. The ETH ATMF they are compared against is an annualised vol. The
// MULTIPLICATIVE ratio is dimensionless so this factor cancels, but the ADDITIVE
// spread is added directly to ethAtmf and so MUST be annualised into its units.
const ANNUALISE_DAILY = Math.sqrt(365);
const ANNUALISE_HOURLY = Math.sqrt(365 * HOURS_PER_DAY);

// Time-to-expiry in years (ACT/365) for a pillar, measured from the as-of
// instant to expiry at 08:00 UTC on the pillar's calendar day. Expiry keys are
// ISO UTC dates (YYYY-MM-DD); the 08:00 UTC settle is the house convention.
export const pillarYearFraction = (expiryKey: string, asOfMs: number): number => {
  const [y, m, d] = expiryKey.slice(0, 10).split('-').map(Number);
  const expiryMs = Date.UTC(y, m - 1, d, 8, 0, 0);
  return (expiryMs - asOfMs) / YEAR_MS;
};

// Compact time-to-expiry from a year-fraction (ACT/365), for the term-structure
// table — more legible than the raw decimal T. The fraction is taken back to
// whole minutes on a 365-day year and bucketed:
//   >= 1 day -> "{d}d {h}h"
//   < 1 day  -> "{h}h {m}m"
//   < 1 hour -> "{m}m"
// Non-finite/null renders "—"; an already-settled pillar (T <= 0) clamps to "0m".
const MINUTES_PER_DAY = 24 * 60;

export const formatExpiryIn = (yearFraction: number | null | undefined): string => {
  if (yearFraction == null || !Number.isFinite(yearFraction)) return '—';
  const totalMinutes = Math.max(0, Math.round(yearFraction * 365 * MINUTES_PER_DAY));
  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((totalMinutes % MINUTES_PER_DAY) / 60);
  const minutes = totalMinutes % 60;
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// Linear-in-T ATM scale across the pillar grid: short_ratio at the front pillar
// (min T), long_ratio at the back (max T). A degenerate single-T grid is all
// short_ratio.
export const linearScale = (ts: number[], shortRatio: number, longRatio: number): number[] => {
  if (ts.length === 0) return [];
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const t of ts) {
    if (t < tMin) tMin = t;
    if (t > tMax) tMax = t;
  }
  const span = tMax - tMin;
  if (span === 0) return ts.map(() => shortRatio);
  return ts.map((t) => shortRatio + (longRatio - shortRatio) * ((t - tMin) / span));
};

// Geometric (log-linear) ATM scale across the pillar grid: interpolates the
// LOG of the ratio linearly in T, short_ratio at the front pillar to long_ratio
// at the back — scale(T) = short * (long/short)^u, u = (T-tMin)/span. Because a
// vol ratio is multiplicative, this is the natural interpolation and is convex
// when short > long, so the belly/back decays toward long_ratio faster than the
// straight line. NOT the reference methodology (the generator is linear) — an
// authorised exploratory alternative, selectable in the sandbox. Falls back to
// linearScale when either ratio is non-positive or non-finite (log undefined),
// which also carries a null/NaN ratio straight through as NaN like linearScale.
export const geometricScale = (ts: number[], shortRatio: number, longRatio: number): number[] => {
  if (ts.length === 0) return [];
  if (
    !(shortRatio > 0) ||
    !(longRatio > 0) ||
    !Number.isFinite(shortRatio) ||
    !Number.isFinite(longRatio)
  )
    return linearScale(ts, shortRatio, longRatio);
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const t of ts) {
    if (t < tMin) tMin = t;
    if (t > tMax) tMax = t;
  }
  const span = tMax - tMin;
  if (span === 0) return ts.map(() => shortRatio);
  return ts.map((t) => shortRatio * Math.pow(longRatio / shortRatio, (t - tMin) / span));
};

// Power ATM scale: interpolate linearly in T^exponent between short_ratio (front)
// and long_ratio (back). exponent = 1 is the straight line (reference); exponent
// = 0.5 interpolates in sqrt(T) — a front-weighted decay that pulls the belly/back
// down toward long_ratio, matching how a vol term structure moves in sqrt time;
// exponent < 1 decays faster, > 1 bulges. Exploratory, multiplicative only. Falls
// back to linear for a non-finite/non-positive exponent; null/NaN ratios flow
// through as NaN as in linearScale. Assumes T >= 0 pillars (the ETH surface grid):
// a negative T (a just-settled pillar) is floored to 0 — i.e. pinned at the front
// — since a fractional power of a negative is undefined; geometricScale, being
// linear in raw T, does not need that floor, so the two can differ on a sub-zero
// pillar.
export const powerScale = (
  ts: number[],
  shortRatio: number,
  longRatio: number,
  exponent: number,
): number[] => {
  if (ts.length === 0) return [];
  if (!(exponent > 0) || !Number.isFinite(exponent)) return linearScale(ts, shortRatio, longRatio);
  const pw = (t: number) => Math.pow(Math.max(t, 0), exponent);
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const t of ts) {
    if (t < tMin) tMin = t;
    if (t > tMax) tMax = t;
  }
  const lo = pw(tMin);
  const span = pw(tMax) - lo;
  if (span === 0) return ts.map(() => shortRatio);
  return ts.map((t) => shortRatio + (longRatio - shortRatio) * ((pw(t) - lo) / span));
};

// Linear interpolation of a BTC discount factor at time-to-expiry `t`, matching
// numpy.interp: clamped to the endpoint value outside the sampled T range.
export const interpDiscountFactor = (
  points: Array<{ T: number; DF: number }>,
  t: number,
): number | null => {
  const pts = points
    .filter((p) => Number.isFinite(p.T) && Number.isFinite(p.DF))
    .sort((a, b) => a.T - b.T);
  if (pts.length === 0) return null;
  if (t <= pts[0].T) return pts[0].DF;
  const last = pts[pts.length - 1];
  if (t >= last.T) return last.DF;
  for (let i = 1; i < pts.length; i += 1) {
    if (t <= pts[i].T) {
      const a = pts[i - 1];
      const b = pts[i];
      const w = (t - a.T) / (b.T - a.T);
      return a.DF + w * (b.DF - a.DF);
    }
  }
  return last.DF;
};

// ---------------------------------------------------------------------------
// Surface payload extraction
// ---------------------------------------------------------------------------

export interface SurfaceTenor {
  T?: number;
  ATMF?: number;
  DF?: number;
  Future?: number;
}

export type SurfaceTenors = Record<string, SurfaceTenor>;

// The latest_vol_surface payload is wrapped differently for a push
// (params.data) versus a one-shot reply (result); either unwraps to an object
// carrying a `tenors` map keyed by expiry date and a build timestamp.
const unwrapSurface = (surface: unknown): Record<string, unknown> | null => {
  if (!surface || typeof surface !== 'object') return null;
  const s = surface as Record<string, unknown>;
  const params = s.params as Record<string, unknown> | undefined;
  const data =
    (params?.data as Record<string, unknown> | undefined) ??
    (s.result as Record<string, unknown> | undefined) ??
    s;
  return data ?? null;
};

export const extractTenors = (surface: unknown): SurfaceTenors | null => {
  const data = unwrapSurface(surface);
  const tenors = data?.tenors;
  return tenors && typeof tenors === 'object' ? (tenors as SurfaceTenors) : null;
};

// Per-expiry published-surface ATM + smile, pulled straight off the payload for
// the Modelled-vs-Published join. ATMF and the smile fields are decimals on the
// wire (e.g. 0.7928 → 79.28%); Future is a price. Any field absent or
// non-finite is null so the join can render a "—" without dropping the row.
//
// Both smile representations are carried because the published payload carries
// both (see volatility-surface-content.tsx, whose PUT/CALL vs RR/FLY toggle only
// shows/hides columns of a single tenor object — it never derives one from the
// other): the delta vols 25P/25C/10P/10C AND the risk-reversal / butterfly pairs
// 25RR/25FLY/10RR/10FLY. The join's C/P | RR/FLY view switches between the two
// with no maths.
export interface PublishedAtm {
  Future: number | null;
  ATMF: number | null;
  '25P': number | null;
  '25C': number | null;
  '10P': number | null;
  '10C': number | null;
  '25RR': number | null;
  '25FLY': number | null;
  '10RR': number | null;
  '10FLY': number | null;
}

export type PublishedAtmByExpiry = Record<string, PublishedAtm>;

// The published smile columns the join surfaces, per representation, in display
// order. Field names are the exact wire keys (see volatility-surface-content.tsx,
// which renders the same set). 'Future' and 'ATMF' are split out — Future is a
// price, ATMF is shown in its own column outside the smile toggle.
export const PUBLISHED_RR_FLY_KEYS = ['25RR', '25FLY', '10RR', '10FLY'] as const;
export const PUBLISHED_CP_KEYS = ['25P', '25C', '10P', '10C'] as const;

const finiteOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

// Pulls the published ATM + wing columns per expiry from a raw surface payload.
// Pure and transport-free: reuses extractTenors to unwrap the push/reply shapes,
// then reads the fields by their wire names. Returns an empty map (never null)
// when the surface carries no tenors, so callers join against {} without a guard.
export const extractPublishedAtm = (surface: unknown): PublishedAtmByExpiry => {
  const tenors = extractTenors(surface);
  if (!tenors) return {};
  const out: PublishedAtmByExpiry = {};
  for (const [expiry, tenor] of Object.entries(tenors)) {
    const t = tenor as Record<string, unknown>;
    out[expiry] = {
      Future: finiteOrNull(t.Future),
      ATMF: finiteOrNull(t.ATMF),
      '25P': finiteOrNull(t['25P']),
      '25C': finiteOrNull(t['25C']),
      '10P': finiteOrNull(t['10P']),
      '10C': finiteOrNull(t['10C']),
      '25RR': finiteOrNull(t['25RR']),
      '25FLY': finiteOrNull(t['25FLY']),
      '10RR': finiteOrNull(t['10RR']),
      '10FLY': finiteOrNull(t['10FLY']),
    };
  }
  return out;
};

export const extractAsOf = (surface: unknown): string | null => {
  const data = unwrapSurface(surface);
  if (!data) return null;
  const raw = (data.timestamp ?? data.snapshot_time) as string | number | undefined;
  return raw == null ? null : String(raw);
};

// Parses a surface build timestamp to epoch-ms. Accepts ISO-8601 with a zone, a
// naive "YYYY-MM-DD HH:MM:SS" (UTC by convention), or an epoch number. Naive
// strings are read as UTC, never local — all displayed/derived times are UTC.
export const parseAsOfMs = (raw: string | number | null | undefined): number | null => {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? (raw > 1e12 ? raw : raw * 1000) : null;
  let s = raw.trim().replace(' ', 'T');
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
};

// ---------------------------------------------------------------------------
// Full computation
// ---------------------------------------------------------------------------

// The premium assumption that maps ETH ATMF to the token ATMF:
//   'multiplicative' (reference/default): tokenAtmf = ethAtmf * scale(T)
//   'additive'       (alternative):       tokenAtmf = ethAtmf + spread(T)
// Both blend linearly in T between a short (front-pillar) and a long
// (back-pillar) endpoint; only the operator and the endpoint units differ.
export type AltSurfaceMode = 'multiplicative' | 'additive';

export const ALT_SURFACE_DEFAULT_MODE: AltSurfaceMode = 'multiplicative';

// The return frequency the realised-vol pipeline runs on:
//   'daily'  (reference/default): the generator's basis — hourly candles
//            resampled to UTC daily-last, daily LOG returns, rolling(window
//            in days), EWMA(half-lives in days), annualised ×√365.
//   'hourly' (exploratory): the raw hourly series, no daily resample — hourly
//            LOG returns, with the day-denominated params converted to hourly
//            PERIODS (window×24, half-lives×24) and annualised ×√(365×24).
// The per-period std ratios are frequency-robust (annualisation cancels), but
// token and ETH differ in their intraday-vs-daily pattern, so the surface the
// ratios generate does change with frequency — which is the point.
export type AltSurfaceFrequency = 'daily' | 'hourly';

export const ALT_SURFACE_DEFAULT_FREQUENCY: AltSurfaceFrequency = 'hourly';

// The shape of the multiplicative ATM scale between the front (short_ratio) and
// back (long_ratio) pillars:
//   'linear'    (reference/default): straight line in T — the generator's basis.
//   'geometric' (exploratory):       log-linear in T, scale = short*(long/short)^u,
//                                     a convex decay that pulls the belly/back down
//                                     toward long_ratio faster than the line.
//   'power'     (exploratory):       interpolate linearly in T^exponent; exponent
//                                     0.5 = sqrt(T) (front-weighted decay), 1 = linear.
// Applies to the multiplicative scale only; the additive spread stays linear
// (a spread can be <= 0, so log interpolation is undefined for it).
export type AltSurfaceScaleCurve = 'linear' | 'geometric' | 'power';

export const ALT_SURFACE_DEFAULT_SCALE_CURVE: AltSurfaceScaleCurve = 'linear';

// Default exponent for the 'power' scale curve: 0.5 interpolates in sqrt(T).
export const ALT_SURFACE_DEFAULT_SCALE_EXPONENT = 0.5;

export interface AltSurfacePillar {
  expiry: string;
  T: number;
  // Multiplicative scale(T) — the token/ETH ratio blended across the grid.
  scale: number;
  // Additive spread(T) in ANNUALISED vol (decimal), same units as ethAtmf — the
  // token-minus-ETH hvol spread blended across the grid.
  spread: number;
  ethAtmf: number;
  tokenAtmf: number;
  future: number | null;
  df: number | null;
  // Diagnostic only: the published token ATMF at the same expiry, and the
  // computed-minus-published difference in vol points. NOT an acceptance gate.
  publishedAtmf: number | null;
  diffVolPts: number | null;
}

export interface AltSurfaceComputation {
  mode: AltSurfaceMode;
  // The return frequency this computation ran on. The hvol* fields below are
  // ANNUALISED with the frequency-appropriate factor, so callers format them
  // directly (no further ×√N).
  frequency: AltSurfaceFrequency;
  hvolShortToken: number;
  hvolShortEth: number;
  hvolLongToken: number;
  hvolLongEth: number;
  // token/ETH realised-vol ratio at each half-life. null when the ETH
  // denominator is zero or non-finite — "no data", distinct from a real figure.
  shortRatio: number | null;
  longRatio: number | null;
  // Additive-mode endpoints: token-minus-ETH hvol spread, ANNUALISED (decimal
  // vol), front (short) and back (long). Always computed so the metrics can
  // show the mode-appropriate figure without recompute.
  shortSpread: number;
  longSpread: number;
  perPillar: AltSurfacePillar[];
  // Diagnostic summary over pillars that have a published counterpart.
  maxAbsDiffVolPts: number | null;
  meanAbsDiffVolPts: number | null;
}

export interface AltSurfaceComputeArgs {
  dailyCloses: DailyCloseRow[];
  // The raw hourly wide frame, consumed ONLY when frequency is 'hourly'. Omit it
  // (or leave it empty) on the daily path — it is never read there.
  hourlyCloses?: DailyCloseRow[];
  params: AltSurfaceParams;
  ethSurface: unknown;
  btcSurface: unknown;
  publishedTokenSurface: unknown;
  tokenSpot: number | null;
  // As-of instant for the term structure. When omitted it falls back to the
  // published token surface build timestamp (the reference's "as-of").
  asOf?: string | number | null;
  // Premium assumption. Defaults to 'multiplicative' — the reference
  // methodology, unchanged. 'additive' is the authorised alternative.
  mode?: AltSurfaceMode;
  // Return frequency. Defaults to 'daily' — the generator's basis, unchanged.
  // 'hourly' runs the pipeline on the raw hourly series.
  frequency?: AltSurfaceFrequency;
  // Multiplicative scale shape. Defaults to 'linear' — the generator's basis.
  // 'geometric' is the exploratory log-linear decay; 'power' interpolates in
  // T^scaleExponent (multiplicative mode only; the additive spread is always linear).
  scaleCurve?: AltSurfaceScaleCurve;
  // Exponent for the 'power' scale curve (default 0.5 = sqrt(T)). Ignored by the
  // other curves.
  scaleExponent?: number;
}

export const columnCloses = (rows: DailyCloseRow[], column: string): number[] =>
  rows.map((row) => row.values[column]);

// token/ETH realised-vol ratio with a guarded denominator. A zero or non-finite
// denominator (e.g. a flat ETH series, or too few closes to score a std) has no
// meaningful ratio, so we return null rather than letting Inf/NaN propagate into
// scale → tokenAtmf. The normal path is the plain quotient, unchanged.
const ratioOrNull = (num: number, den: number): number | null =>
  Number.isFinite(den) && den !== 0 ? num / den : null;

// Runs the full ATM pipeline. Returns null when the irreducible inputs are
// missing (no daily closes, or no ETH reference surface) — the caller renders
// an empty/loading state rather than a half-computed surface.
export const computeAltSurface = (args: AltSurfaceComputeArgs): AltSurfaceComputation | null => {
  const { dailyCloses, params, ethSurface, btcSurface, publishedTokenSurface, tokenSpot } = args;
  const { windowSize, hlShort, hlLong } = params;
  // The long leg gets its own rolling window; it defaults to windowSize (the
  // reference, where both legs share one window) when not set.
  const windowLong = params.windowLong ?? windowSize;
  const mode: AltSurfaceMode = args.mode ?? ALT_SURFACE_DEFAULT_MODE;
  const frequency: AltSurfaceFrequency = args.frequency ?? ALT_SURFACE_DEFAULT_FREQUENCY;
  const scaleCurve: AltSurfaceScaleCurve = args.scaleCurve ?? ALT_SURFACE_DEFAULT_SCALE_CURVE;
  const scaleExponent: number = args.scaleExponent ?? ALT_SURFACE_DEFAULT_SCALE_EXPONENT;

  // Frequency selects the close frame, the day→period multiplier for the
  // day-denominated params, and the annualisation factor. On 'daily' the
  // multiplier is 1 and nothing below changes from the reference — the daily
  // numbers stay bit-identical.
  const hourly = frequency === 'hourly';
  const closesFrame = hourly ? (args.hourlyCloses ?? []) : dailyCloses;
  const periodsPerDay = hourly ? HOURS_PER_DAY : 1;
  const annualise = hourly ? ANNUALISE_HOURLY : ANNUALISE_DAILY;
  const windowShortPeriods = windowSize * periodsPerDay;
  const windowLongPeriods = windowLong * periodsPerDay;
  const hlShortPeriods = hlShort * periodsPerDay;
  const hlLongPeriods = hlLong * periodsPerDay;

  const ethTenors = extractTenors(ethSurface);
  if (!ethTenors || closesFrame.length === 0) return null;

  const ethCloses = columnCloses(closesFrame, DAILY_CLOSE_COLUMNS.ETH);
  const tokenCloses = columnCloses(closesFrame, DAILY_CLOSE_COLUMNS.TOKEN);

  // Per-period (unannualised) realised std at each half-life — the ratios and
  // spreads derive from these, so annualisation cancels in the multiplicative
  // ratio exactly as it did on the daily-only path.
  const stdShortEth = historicalVolLast(ethCloses, windowShortPeriods, hlShortPeriods);
  const stdShortToken = historicalVolLast(tokenCloses, windowShortPeriods, hlShortPeriods);
  const stdLongEth = historicalVolLast(ethCloses, windowLongPeriods, hlLongPeriods);
  const stdLongToken = historicalVolLast(tokenCloses, windowLongPeriods, hlLongPeriods);

  const shortRatio = ratioOrNull(stdShortToken, stdShortEth);
  const longRatio = ratioOrNull(stdLongToken, stdLongEth);

  // Annualised hvols for the metrics/overlay (frequency-aware factor), so the
  // display just formats them.
  const hvolShortEth = stdShortEth * annualise;
  const hvolShortToken = stdShortToken * annualise;
  const hvolLongEth = stdLongEth * annualise;
  const hvolLongToken = stdLongToken * annualise;

  // Additive endpoints: token-minus-ETH hvol spread, annualised into ethAtmf's
  // units (annualised decimal vol). The multiplicative ratio above is
  // dimensionless so it needs no annualisation; the spread is added to ethAtmf
  // directly and so does.
  const shortSpread = (stdShortToken - stdShortEth) * annualise;
  const longSpread = (stdLongToken - stdLongEth) * annualise;

  const asOfMs = parseAsOfMs(args.asOf ?? null) ?? parseAsOfMs(extractAsOf(publishedTokenSurface));

  // ETH pillar grid in payload order — row order carries the term structure, so
  // it is never re-sorted.
  const ethKeys = Object.keys(ethTenors);
  const ts = ethKeys.map((key) => {
    const recomputed = asOfMs == null ? NaN : pillarYearFraction(key, asOfMs);
    // Fall back to the payload T only if the as-of is unavailable.
    return Number.isFinite(recomputed) ? recomputed : (ethTenors[key].T ?? NaN);
  });
  // A null ratio flows into the scale as NaN so scale → tokenAtmf renders "—"
  // downstream, consistent with the metrics showing "no data" for the ratio.
  const scales =
    scaleCurve === 'geometric'
      ? geometricScale(ts, shortRatio ?? NaN, longRatio ?? NaN)
      : scaleCurve === 'power'
        ? powerScale(ts, shortRatio ?? NaN, longRatio ?? NaN, scaleExponent)
        : linearScale(ts, shortRatio ?? NaN, longRatio ?? NaN);
  const spreads = linearScale(ts, shortSpread, longSpread);

  const btcTenors = extractTenors(btcSurface);
  const btcPoints: Array<{ T: number; DF: number }> = btcTenors
    ? Object.values(btcTenors)
        .filter(
          (v): v is Required<Pick<SurfaceTenor, 'T' | 'DF'>> & SurfaceTenor =>
            Number.isFinite(v.T) && Number.isFinite(v.DF),
        )
        .map((v) => ({ T: v.T as number, DF: v.DF as number }))
    : [];

  const publishedTenors = extractTenors(publishedTokenSurface);

  const perPillar: AltSurfacePillar[] = ethKeys.map((expiry, i) => {
    const T = ts[i];
    const scale = scales[i];
    const spread = spreads[i];
    const ethAtmf = ethTenors[expiry].ATMF ?? NaN;
    const tokenAtmf = mode === 'additive' ? ethAtmf + spread : ethAtmf * scale;
    const df = btcPoints.length > 0 ? interpDiscountFactor(btcPoints, T) : null;
    const future = tokenSpot != null && df != null && df !== 0 ? tokenSpot / df : null;
    const publishedAtmf = publishedTenors?.[expiry]?.ATMF ?? null;
    const diffVolPts =
      publishedAtmf != null && Number.isFinite(tokenAtmf)
        ? (tokenAtmf - publishedAtmf) * 100
        : null;
    return { expiry, T, scale, spread, ethAtmf, tokenAtmf, future, df, publishedAtmf, diffVolPts };
  });

  const absDiffs = perPillar
    .map((p) => p.diffVolPts)
    .filter((d): d is number => d != null && Number.isFinite(d))
    .map(Math.abs);
  const maxAbsDiffVolPts = absDiffs.length > 0 ? Math.max(...absDiffs) : null;
  const meanAbsDiffVolPts =
    absDiffs.length > 0 ? absDiffs.reduce((a, b) => a + b, 0) / absDiffs.length : null;

  return {
    mode,
    frequency,
    hvolShortToken,
    hvolShortEth,
    hvolLongToken,
    hvolLongEth,
    shortRatio,
    longRatio,
    shortSpread,
    longSpread,
    perPillar,
    maxAbsDiffVolPts,
    meanAbsDiffVolPts,
  };
};
